import { placesToInput, savedPlaceKeys } from './nc-places';
import type {
  DayQualityChecklistView,
  DayQualityFrontView,
  DayQualityServiceView,
} from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import {
  MIN_NON_CONFORMITY_DESCRIPTION,
  MIN_NON_CONFORMITY_SOLUTION,
  ownerKeyOf,
  type NonConformity,
} from '@obraplan/core/domain/quality/non-conformity';
import {
  nonConformitiesOfAnswer,
  type VerificationResult,
} from '@obraplan/core/domain/quality/service-verification';

/**
 * O rascunho da verificação de qualidade de uma frente, e o que ele vira (Fase 19).
 *
 * Fora do componente por dois motivos: o diálogo precisa das mesmas funções para saber se
 * pode salvar, e um arquivo que exporta componente e função ao mesmo tempo perde o
 * recarregamento a quente.
 *
 * ## A NC é desta medição (Fase 37, RN-474)
 *
 * A checklist é uma por frente × serviço × dia, mas a NC é da medição que a abriu. O rascunho
 * guarda só a NC **desta** medição — a pessoa da conferência, a equipe, o lançamento do RDO —,
 * e marca o item que já tem NC de outra (`fromOthers`): ele continua reprovado, e esta medição
 * só abre a sua ali se também encontrou algo (`own`).
 */

export interface AnswerDraft {
  readonly result?: VerificationResult;
  /** O que foi encontrado — só se reprovado por esta medição. */
  readonly description: string;
  /** O que foi feito para corrigir (RN-294) — opcional desde a Fase 37 (RN-476). */
  readonly solution: string;
  /** A quantidade não conforme, como digitada — opcional (RN-473). */
  readonly quantity: string;
  /** As fotos escolhidas antes de a NC existir — sobem depois de gravar (RN-479). */
  readonly photos: readonly File[];
  /** Os cômodos e as peças do dia marcados na NC (Fase 37, D9), pela chave de `nc-places`. */
  readonly places: readonly string[];
  /** O item já tinha NC viva de outra medição quando o rascunho nasceu (RN-474). */
  readonly fromOthers: boolean;
  /** No item de outra medição: esta também encontrou não conformidade ali. */
  readonly own: boolean;
}

export const EMPTY_ANSWER: AnswerDraft = {
  description: '',
  solution: '',
  quantity: '',
  photos: [],
  places: [],
  fromOthers: false,
  own: false,
};

export interface ChecklistDraft {
  /** A ficha se aplica hoje? Um serviço pode ter duas, e só uma cabe no dia. */
  readonly applies: boolean;
  readonly answers: Readonly<Record<string, AnswerDraft>>;
}

export interface ServiceDraft {
  /** O serviço foi executado hoje nesta frente. */
  readonly selected: boolean;
  readonly checklists: Readonly<Record<string, ChecklistDraft>>;
}

/** Por serviço da frente. */
export type FrontDraft = Readonly<Record<string, ServiceDraft>>;

/** O que o caso de uso recebe por `(frente, serviço)`. */
export interface VerificationInput {
  readonly activityId: string;
  readonly serviceId: string;
  readonly checklists: readonly {
    readonly checklistId: string;
    readonly answers: readonly {
      readonly itemId: string;
      readonly result: VerificationResult;
      readonly nonConformity?: {
        readonly description: string;
        readonly solution?: string;
        readonly quantity?: number;
      };
    }[];
  }[];
}

/**
 * O dono das NCs desta medição em cada serviço da frente — `A:<alocação>` ou `T:<medição da
 * equipe>` (`ownerKey` do domínio). `undefined` é "sem medição a quem pertencer", e aí a NC
 * sem dono (de antes da `0084`) é a própria, como no servidor.
 */
export type OwnerOf = (serviceId: string) => string | undefined;

/** A medição que ainda não existe (o lançamento do RDO): nenhuma NC do dia é dela. */
export const NEW_MEASUREMENT_OWNER = 'NOVA';

/** `1.234,5` ou `1234.5` → número; `''` → `undefined`; o resto → `null` (inválido). */
export function parseQuantityText(text: string): number | undefined | null {
  const limpo = text.trim().replace(/\s/g, '');
  if (limpo === '') return undefined;
  const normalizado =
    limpo.includes(',') && limpo.lastIndexOf(',') > limpo.lastIndexOf('.')
      ? limpo.replace(/\./g, '').replace(',', '.')
      : limpo.replace(/,/g, '');
  const numero = Number(normalizado);
  return Number.isFinite(numero) && numero > 0 ? numero : null;
}

/** O número como o campo o mostra: vírgula, sem casas inúteis. */
export function formatQuantityText(value: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false });
}

/**
 * O rascunho inicial de uma frente: o que já foi verificado hoje, pré-preenchido.
 *
 * Serviço já verificado nasce marcado e com as respostas de quem verificou — e com a NC
 * **desta medição** no item (descrição, solução, quantidade), lida da NC e não da resposta,
 * porque é lá que ela mora. Serviço com uma ficha só nasce com ela aplicada: escolher a única
 * opção não é decisão de ninguém.
 */
export function initialFrontDraft(
  front: DayQualityFrontView,
  nonConformities: readonly NonConformity[],
  /**
   * O serviço já escolhido antes de abrir — o do lançamento do RDO.
   *
   * Ele nasce **marcado**: quem escolheu a atividade no formulário já declarou o que fez, e
   * pedir a mesma marca de novo é pedir a mesma escolha duas vezes.
   */
  serviceId?: string,
  /** O dono das NCs desta medição por serviço (Fase 37). Ausente = sem medição. */
  ownerOf?: OwnerOf,
): FrontDraft {
  const vivas = nonConformities.filter((nc) => nc.status !== 'CANCELLED');
  const draft: Record<string, ServiceDraft> = {};

  for (const service of front.services) {
    const verification = service.verification;
    const dono = ownerOf?.(service.serviceId);
    const checklists: Record<string, ChecklistDraft> = {};
    for (const checklist of service.checklists) {
      const respondida = verification?.checklists.find(
        (item) => item.checklistId === checklist.checklistId,
      );
      const answers: Record<string, AnswerDraft> = {};
      for (const answer of respondida?.answers ?? []) {
        const doItem =
          verification === undefined
            ? []
            : nonConformitiesOfAnswer(vivas, verification.id, checklist.checklistId, answer);
        const propria = doItem.find((nc) => ownerKeyOf(nc) === dono);
        const deOutras = doItem.some((nc) => ownerKeyOf(nc) !== dono);
        answers[answer.itemId] = {
          ...EMPTY_ANSWER,
          result: answer.result,
          description: propria?.description ?? '',
          // A NC aberta, ou a de antes da solução, volta com o campo vazio (RN-476).
          solution: propria?.solution ?? '',
          quantity: propria?.quantity === undefined ? '' : formatQuantityText(propria.quantity),
          places: savedPlaceKeys(propria),
          fromOthers: deOutras,
          own: propria !== undefined,
        };
      }
      checklists[checklist.checklistId] = {
        applies:
          respondida !== undefined ||
          (verification === undefined && service.checklists.length === 1),
        answers,
      };
    }
    draft[service.serviceId] = {
      selected: verification !== undefined || service.serviceId === serviceId,
      checklists,
    };
  }

  return draft;
}

/** A NC desta medição no item: reprovado por ela, e não só pelas outras. */
export function opensOwnNonConformity(answer: AnswerDraft | undefined): boolean {
  return answer?.result === 'REJECTED' && (!answer.fromOthers || answer.own);
}

function answerComplete(answer: AnswerDraft | undefined): boolean {
  if (answer?.result === undefined) return false;
  if (!opensOwnNonConformity(answer)) return true;
  // A NC desta medição: o que foi encontrado é obrigatório; a solução e a quantidade, não —
  // mas, escritas, valem a régua delas (RN-473, RN-476).
  const solucao = answer.solution.trim();
  return (
    answer.description.trim().length >= MIN_NON_CONFORMITY_DESCRIPTION &&
    (solucao === '' || solucao.length >= MIN_NON_CONFORMITY_SOLUTION) &&
    parseQuantityText(answer.quantity) !== null
  );
}

function checklistComplete(
  checklist: DayQualityChecklistView,
  draft: ChecklistDraft | undefined,
): boolean {
  if (draft === undefined || !draft.applies) return false;
  return checklist.items.every((item) => answerComplete(draft.answers[item.id]));
}

/** RN-219 na tela: serviço com ficha precisa de ao menos uma aplicada, e ela inteira. */
export function isServiceDraftComplete(
  service: DayQualityServiceView,
  draft: ServiceDraft | undefined,
): boolean {
  if (draft === undefined || !draft.selected) return false;
  if (service.checklists.length === 0) return true;
  const aplicadas = service.checklists.filter(
    (checklist) => draft.checklists[checklist.checklistId]?.applies === true,
  );
  if (aplicadas.length === 0) return false;
  return aplicadas.every((checklist) =>
    checklistComplete(checklist, draft.checklists[checklist.checklistId]),
  );
}

/** RN-218 na tela: ao menos um serviço declarado, e cada um deles completo. */
export function isFrontDraftComplete(front: DayQualityFrontView, draft: FrontDraft): boolean {
  const selecionados = front.services.filter(
    (service) => draft[service.serviceId]?.selected === true,
  );
  if (selecionados.length === 0) return false;
  return selecionados.every((service) => isServiceDraftComplete(service, draft[service.serviceId]));
}

/** Alguma verificação já feita hoje nesta frente — por quem quer que seja (ADR-128). */
export function frontHasVerification(front: DayQualityFrontView): boolean {
  return front.services.some((service) => service.verification !== undefined);
}

/** As respostas das fichas aplicadas de um serviço marcado — o miolo das duas saídas abaixo. */
function respostasAplicadas(
  front: DayQualityFrontView,
  draft: FrontDraft,
): {
  readonly service: DayQualityServiceView;
  readonly checklists: readonly {
    readonly checklist: DayQualityChecklistView;
    readonly answers: readonly { readonly itemId: string; readonly answer: AnswerDraft }[];
  }[];
}[] {
  return front.services.flatMap((service) => {
    const serviceDraft = draft[service.serviceId];
    if (serviceDraft === undefined || !serviceDraft.selected) return [];
    return [
      {
        service,
        checklists: service.checklists.flatMap((checklist) => {
          const checklistDraft = serviceDraft.checklists[checklist.checklistId];
          if (checklistDraft === undefined || !checklistDraft.applies) return [];
          return [
            {
              checklist,
              answers: checklist.items.flatMap((item) => {
                const answer = checklistDraft.answers[item.id];
                return answer?.result === undefined ? [] : [{ itemId: item.id, answer }];
              }),
            },
          ];
        }),
      },
    ];
  });
}

/** O rascunho vira a entrada do caso de uso: só os serviços marcados e as fichas aplicadas. */
export function frontDraftToInput(
  front: DayQualityFrontView,
  draft: FrontDraft,
): readonly VerificationInput[] {
  return respostasAplicadas(front, draft).map(({ service, checklists }) => ({
    activityId: front.activityId,
    serviceId: service.serviceId,
    checklists: checklists.map(({ checklist, answers }) => ({
      checklistId: checklist.checklistId,
      answers: answers.map(({ itemId, answer }) => {
        const quantidade = parseQuantityText(answer.quantity);
        const solucao = answer.solution.trim();
        return {
          itemId,
          result: answer.result!,
          ...(opensOwnNonConformity(answer)
            ? {
                nonConformity: {
                  description: answer.description.trim(),
                  ...(solucao === '' ? {} : { solution: solucao }),
                  ...(quantidade === undefined || quantidade === null
                    ? {}
                    : { quantity: quantidade }),
                  // Sempre vai, vazio inclusive: tirar o último lugar é dizer "nenhum" (D9).
                  places: placesToInput(answer.places),
                },
              }
            : {}),
        };
      }),
    })),
  }));
}

/** As fotos escolhidas no modal, por item — sobem quando a NC já tem número (RN-479). */
export interface PendingNonConformityPhotos {
  readonly activityId: string;
  readonly serviceId: string;
  readonly checklistId: string;
  readonly itemId: string;
  readonly files: readonly File[];
}

export function pendingPhotos(
  front: DayQualityFrontView,
  draft: FrontDraft,
): readonly PendingNonConformityPhotos[] {
  return respostasAplicadas(front, draft).flatMap(({ service, checklists }) =>
    checklists.flatMap(({ checklist, answers }) =>
      answers.flatMap(({ itemId, answer }) =>
        opensOwnNonConformity(answer) && answer.photos.length > 0
          ? [
              {
                activityId: front.activityId,
                serviceId: service.serviceId,
                checklistId: checklist.checklistId,
                itemId,
                files: answer.photos,
              },
            ]
          : [],
      ),
    ),
  );
}

/** Os números do rascunho, para o selo do passo 1. */
export function summarizeFrontDraft(
  front: DayQualityFrontView,
  draft: FrontDraft,
): { approved: number; rejected: number; total: number } {
  let approved = 0;
  let rejected = 0;
  let total = 0;
  for (const service of front.services) {
    const serviceDraft = draft[service.serviceId];
    if (serviceDraft === undefined || !serviceDraft.selected) continue;
    for (const checklist of service.checklists) {
      const checklistDraft = serviceDraft.checklists[checklist.checklistId];
      if (checklistDraft === undefined || !checklistDraft.applies) continue;
      for (const item of checklist.items) {
        const result = checklistDraft.answers[item.id]?.result;
        if (result === undefined) continue;
        total += 1;
        if (result === 'APPROVED') approved += 1;
        if (result === 'REJECTED') rejected += 1;
      }
    }
  }
  return { approved, rejected, total };
}
